<template>
  <div
    v-if="!loading"
    class="popup-holder popup-import"
    :style="[showImportPopup ? '' : 'display:none']"
  >
    <div class="popup-wrap">
      <div class="popup-head">
        <h2>Transcribe Audio and Video Files</h2>
        <a
          @click="closeImportPopup"
          href="#"
          class="popup-close icon-close"
        ></a>
      </div>
      <div class="popup-body">
        <div class="">
          <!-- Conditionally render Uppy dashboard or no uploads remaining message -->
          <div v-if="!hasExceededLimit">
            <!--
              Unfinished uploads. Resume already works — the uploader probes GCS
              for the confirmed offset and continues — but it only fires when the
              user re-adds the same file, which nobody knows to do. Saying so here,
              where they are about to pick a file, is the whole point.
            -->
            <div
              v-if="unfinishedUploads.length"
              class="resume-notice"
              data-testid="resume-notice"
            >
              <p class="resume-notice__title">Pick up where you left off</p>
              <p class="resume-notice__body">{{ resumeNoticeBody }}</p>
              <ul class="resume-notice__list">
                <li
                  v-for="item in visibleUnfinishedUploads"
                  :key="item.fingerprint"
                >
                  <span class="resume-notice__file">{{ item.fileName }}</span>
                  <span v-if="item.pendingFinalize" class="resume-notice__hint">
                    — already uploaded, just needs finishing
                  </span>
                </li>
              </ul>
              <p v-if="hiddenUnfinishedCount" class="resume-notice__hint">
                and {{ hiddenUnfinishedCount }} more
              </p>
            </div>

            <!-- Language Selection Dropdown -->
            <div class="language-selector-container">
              <label for="language-select" class="language-label">
                Transcription Language
              </label>
              <select
                id="language-select"
                v-model="selectedLanguage"
                class="language-dropdown"
                :disabled="uploadBusy"
                @change="onLanguageChange"
              >
                <option :value="autoDetect">Auto Detect</option>
                <option
                  v-for="option in languageOptions"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </select>
            </div>

            <Dashboard
              :uppy="uppy"
              :props="dashboardProps"
              style="width: 100%; height: 300px"
            />
          </div>
          <div v-else class="exceeded-limit-message">
            <p>
              No more uploads remain for today. Please upgrade to get more file
              uploads.
            </p>
            <router-link
              to="/settings/plans"
              class="btn"
              @click="trackUpgradeClicked"
              >Upgrade now</router-link
            >
          </div>
        </div>
      </div>
      <div class="popup-foot" v-if="!workspaceStore.isSubscribedState">
        <!-- Display remaining uploads -->
        <div class="import-counts">
          <span>{{ remainingFiles }} of {{ dailyLimit }} uploads left</span>
        </div>
        <div class="btn-holder">
          <router-link
            to="/settings/plans"
            class="btn btn-secondary-outline"
            @click="trackUpgradeClicked"
            >Upgrade for unlimited imports</router-link
          >
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  createTempAudioFile,
  getAccessTokenSilentlyOutsideComponent,
} from "@/services/workspace.service";
import { useWorkSpaceStore } from "@/stores/workspace-store";
import { useAppStore } from "@/stores/app-store";
import { useNotificationStore } from "@/stores/notification-store";
import { useLanguagePreferenceStore } from "@/stores/language-preference-store";
import { mapStores } from "pinia";
import { analytics } from "@/services/analytics.service";
import { useIsMobile } from "@/composables/useIsMobile";
import {
  AUTO_DETECT,
  initialLanguage,
  languageForRequest,
  languageOptions,
} from "@/constants/transcriptionLanguages";

import Uppy from "@uppy/core";
import Transloadit from "@uppy/transloadit";
import UppyGcsUploader, {
  listUnfinishedUploads,
} from "@/services/UppyGcsUploader";
import { Dashboard } from "@uppy/vue";

/**
 * Internal architectural ceiling for upload file size. 20 GiB.
 * NOT a user-facing promise — kept in sync with the API's MAX_UPLOAD_SIZE_BYTES
 * constant and the top-level CLAUDE.md architectural limits section. Bumping
 * this here without also bumping the API and processor will cause large uploads
 * to pass the client check only to fail at API validation.
 */
const MAX_FILE_SIZE_BYTES = 20 * 1024 * 1024 * 1024; // 20 GiB

/**
 * Shown when more files are offered than the one-at-a-time pipeline accepts.
 *
 * Uppy's stock copy is "You can only upload 1 file", which reads as an account
 * or plan limit. It is not — it is a client-side batching rule that applies
 * identically to free and paid users. Quota is enforced server-side and
 * surfaces separately as `DailyFileLimitExceeded`.
 *
 * States the outcome only, and deliberately suggests no remedy: there is no
 * action that is correct in every case that reaches here. Uppy counts every
 * retained file, never evicts one after a successful upload, and the Dashboard
 * hides its remove button once a file completes
 * (`showRemoveButtonAfterComplete` defaults to false). So "wait for the upload
 * to finish" is false when the blocking file has already finished, and "remove
 * it" is false when there is no remove button. Freeing that slot is a real
 * fix, not a wording problem.
 *
 * What IS true in every case: Uppy discards the whole offered batch on this
 * restriction (`validFilesToAdd: []`), so nothing the user just picked landed.
 */
const ONE_AT_A_TIME_MESSAGE =
  "Only one file at a time — nothing from this selection was added.";

/**
 * Uppy instances already closed. `close()` cancels everything the instance
 * holds, which emits `cancel-all` — an event this component listens for, so
 * without this the close would call itself back.
 */
const retiredUppyInstances = new WeakSet();

export default {
  name: "ImportPopup",
  props: ["showImportPopup"],
  components: {
    Dashboard,
  },
  computed: {
    ...mapStores(
      useAppStore,
      useWorkSpaceStore,
      useNotificationStore,
      useLanguagePreferenceStore,
    ),
    languageOptions() {
      return languageOptions(this.defaultAtOpen);
    },
    /** Cap the list so a browser holding many stale sessions can't flood the popup. */
    visibleUnfinishedUploads() {
      return this.unfinishedUploads.slice(0, 3);
    },
    hiddenUnfinishedCount() {
      return Math.max(0, this.unfinishedUploads.length - 3);
    },
    resumeNoticeBody() {
      return this.unfinishedUploads.length === 1
        ? "This file didn't finish uploading. Add it again below and it continues from where it stopped — you won't re-send the part that already went through."
        : "These files didn't finish uploading. Add one again below and it continues from where it stopped — you won't re-send the part that already went through.";
    },
    dashboardProps() {
      const base = {
        metaFields: [{ id: "name", name: "Name", placeholder: "File name" }],
        proudlyDisplayPoweredByUppy: false,
      };
      // Phones have no drag-and-drop: drop the "Drop files here or" copy and
      // make the browse action read like the primary button it is styled as.
      if (useIsMobile().isMobile.value) {
        return {
          ...base,
          locale: {
            strings: {
              dropPasteFiles: "%{browseFiles}",
              browseFiles: "Choose an audio or video file",
            },
          },
        };
      }
      return base;
    },
    hasExceededLimit() {
      return this.workspaceStore.hasExceededFileUploadLimit;
    },
    remainingFiles() {
      return this.workspaceStore.remainingFiles;
    },
    uppy() {
      let userUUID = this.appStore?.user?.userUUID;
      let workspaceUUID = this.workspaceStore?.activeWorkspaceUUID;
      // What to send for this upload's language (see languageForRequest).
      const language = languageForRequest(
        this.selectedLanguage,
        this.defaultAtOpen,
      );

      // Native pipeline is now the only path for imports. The Transloadit
      // branch below is retained for rollback only and is unreachable until
      // this constant is flipped back or the flag check is restored.
      const useNativePipeline = true;

      const uppyInstance = new Uppy({
        id: "uppy1",
        autoProceed: true,
        debug: true,
        proudlyDisplayPoweredByUppy: false,
        restrictions: {
          maxNumberOfFiles: 1,
          maxFileSize: MAX_FILE_SIZE_BYTES,
          allowedFileTypes: [
            ".3ga",
            ".8svx",
            ".aac",
            ".ac3",
            ".aif",
            ".aiff",
            ".alac",
            ".amr",
            ".ape",
            ".au",
            ".dss",
            ".flac",
            ".flv",
            ".m4a",
            ".m4b",
            ".m4p",
            ".m4r",
            ".mp3",
            ".mpga",
            ".ogg",
            ".oga",
            ".mogg",
            ".opus",
            ".qcp",
            ".tta",
            ".voc",
            ".wav",
            ".wma",
            ".wv",
            ".mpeg",
            ".mpg",
            ".mov",
            ".mp4",
          ],
        },
        locale: {
          strings: {
            youCanOnlyUploadX: {
              0: ONE_AT_A_TIME_MESSAGE,
              1: "You can only add %{smart_count} files at a time.",
            },
          },
        },
      });

      // Show a friendly toast when Uppy rejects a file due to any
      // restriction (wrong type, too large, etc.). We check `file.size`
      // directly rather than string-matching Uppy's locale-dependent
      // error message, because the locale strings can change across
      // Uppy versions.
      uppyInstance.on("restriction-failed", (file, error) => {
        const size = file?.size;
        const isSizeViolation = size != null && size > MAX_FILE_SIZE_BYTES;
        if (isSizeViolation) {
          const fileSizeGB = (size / (1024 * 1024 * 1024)).toFixed(1);
          this.$toast?.error(
            `"${
              file?.name ?? "File"
            }" is ${fileSizeGB} GB. Files must be 20 GB or smaller. Try splitting the file into smaller segments and uploading them separately.`,
          );
          analytics.track("upload_restriction_failed", {
            reason: "max_file_size",
            file_size: size,
            file_name: file?.name,
          });
        } else if (file == null) {
          // Uppy raises *aggregate* restrictions with no file attached (see
          // Restricter.validateAggregateRestrictions). Of the aggregate
          // restrictions, only `maxNumberOfFiles` is configured above, so a
          // missing file identifies it unambiguously. Adding `maxTotalFileSize`
          // or `minNumberOfFiles` to `restrictions` would break that, and this
          // branch would then need a real discriminator.
          this.$toast?.info?.(ONE_AT_A_TIME_MESSAGE);
          analytics.track("upload_restriction_failed", {
            reason: "max_number_of_files",
            error_message: error?.message,
          });
        } else {
          // Fallback for other restrictions (wrong file type, duplicate)
          this.$toast?.error(error?.message || "This file can't be uploaded.");
          analytics.track("upload_restriction_failed", {
            reason: "other",
            error_message: error?.message,
            file_name: file?.name,
          });
        }
      });

      // Pipeline routing: native GCS upload or Transloadit
      if (useNativePipeline) {
        uppyInstance.use(UppyGcsUploader, {
          apiBaseUrl: import.meta.env.VITE_API_SERVER_URL,
          getAccessToken: getAccessTokenSilentlyOutsideComponent,
          workspaceUUID,
          userUUID,
          language,
        });

        uppyInstance.on("upload-success", async (file, response) => {
          const audioFileUUID = response?.body?.audioFileUUID;
          const duration = response?.body?.duration;

          analytics.track("native_upload_completed", {
            file_name: file.name,
            file_size: file.size,
            audio_file_uuid: audioFileUUID,
            duration,
          });

          this.workspaceStore.fileUploaded = true;
          this.workspaceStore.invalidateDailyFileLimit();
          this.workspaceStore.invalidateAudioFileCache(null, workspaceUUID);
          // The uploader cleared this file's session on finalize; drop it from
          // the notice so a just-completed upload isn't still advertised as
          // unfinished.
          this.refreshUnfinishedUploads();
          await this.workspaceStore.checkDailyFileUploadLimit(true);

          const routeName = this.$route.name;
          if (routeName !== "conversations") {
            setTimeout(() => {
              this.closeImportPopup();
              this.$router.push({
                name: "conversations",
                params: {
                  workspaceUUID: this.workspaceStore.activeWorkspaceUUID,
                },
              });
              this.checkNotificationPrompt();
            }, 1000);
          } else {
            this.closeImportPopup();
            this.checkNotificationPrompt();
          }
        });

        uppyInstance.on("upload-error", (file, error) => {
          analytics.track("native_upload_failed", {
            file_name: file?.name,
            error_message: error?.message || "Unknown error",
          });
          // A failed attempt has just LEFT a resumable session behind. Without
          // this the notice only appears after closing and reopening the
          // popup — exactly when the user is still looking at the failure and
          // most likely to retry.
          this.refreshUnfinishedUploads();
        });
      } else {
        // Existing Transloadit pipeline — unchanged
        const assemblyId = this.workspaceStore?.isSubscribedState
          ? import.meta.env.VITE_TRANSLOADIT_ASSEMBLY_ID
          : import.meta.env.VITE_TRANSLOADIT_FREE_USER_ASSEMBLY_ID;

        uppyInstance
          .use(Transloadit, {
            waitForEncoding: false,
            waitForMetadata: true,
            alwaysRunAssembly: true,
            assemblyOptions: {
              params: {
                template_id: assemblyId,
                notify_url: this.TRANSLOADIT_NOTIFY_URL,
                auth: {
                  key: import.meta.env.VITE_TRANSLOADIT_AUTH_KEY,
                },
              },
              fields: {
                userUUID: userUUID,
                workspaceUUID: workspaceUUID,
                ...(language && { language }),
              },
            },
          })
          .on("transloadit:complete", async (result) => {
            const createTempAudioFileDTO = {
              transloaditAssemblyId: result.assembly_id,
              originalFileName: result?.uploads[0]?.name,
              duration: result?.uploads[0]?.meta?.duration,
              ...(language && { language }),
            };

            await createTempAudioFile(workspaceUUID, createTempAudioFileDTO);
            this.workspaceStore.fileUploaded = true;

            this.workspaceStore.invalidateDailyFileLimit();
            this.workspaceStore.invalidateAudioFileCache(null, workspaceUUID);
            await this.workspaceStore.checkDailyFileUploadLimit(true);

            const routeName = this.$route.name;
            if (routeName !== "conversations") {
              setTimeout(() => {
                this.closeImportPopup();
                this.$router.push({
                  name: "conversations",
                  params: {
                    workspaceUUID: this.workspaceStore.activeWorkspaceUUID,
                  },
                });
                this.checkNotificationPrompt();
              }, 1000);
            } else {
              this.closeImportPopup();
              this.checkNotificationPrompt();
            }
          })
          .on("transloadit:upload", () => {
            // Transloadit upload event
          });
      }

      // Common event handlers for both pipelines
      uppyInstance
        .on("file-added", (file) => {
          analytics.importFileSelected({
            file_name: file.name,
            file_size: file.size,
            file_type: file.type,
          });
        })
        .on("upload", () => {
          const files = uppyInstance.getFiles();
          const file = files[0];
          analytics.importUploadStarted({
            file_name: file?.name,
            file_size: file?.size,
            file_type: file?.type,
            language: this.selectedLanguage || "auto",
            pipeline: useNativePipeline ? "native" : "transloadit",
          });
        })
        .on("complete", (result) => {
          if (result.successful?.length > 0) {
            const file = result.successful[0];
            analytics.importUploadCompleted({
              file_name: file.name,
              file_size: file.size,
              duration_ms: result.uploadDuration || 0,
              pipeline: useNativePipeline ? "native" : "transloadit",
            });
          }
          if (result.failed?.length > 0) {
            const file = result.failed[0];
            analytics.importUploadFailed({
              file_name: file.name,
              error_message: file.error || "Unknown error",
              pipeline: useNativePipeline ? "native" : "transloadit",
            });
          }
        });

      // Lock the language picker while this instance holds a file that's
      // still going: changing the language rebuilds the instance.
      const syncBusy = () => this.syncUploadBusy(uppyInstance);
      [
        "file-added",
        "file-removed",
        "upload-success",
        "upload-error",
        "complete",
        "cancel-all",
      ].forEach((event) => uppyInstance.on(event, syncBusy));

      return uppyInstance;
    },
  },
  data() {
    return {
      TRANSLOADIT_NOTIFY_URL: import.meta.env.VITE_TRANSLOADIT_NOTIFY_URL,
      dailyLimit: 3, // Set your daily limit here, adjust as needed
      loading: true, // Add a loading state
      // This upload's language; starts from the saved default on open.
      selectedLanguage: AUTO_DETECT,
      // The saved default as of this open (undefined until it loads).
      defaultAtOpen: undefined,
      // Set once the user picks a language, so a late-arriving default can't
      // overwrite their choice.
      languageTouched: false,
      autoDetect: AUTO_DETECT,
      // A file in the current Uppy instance is still being added or
      // uploaded: the language picker is locked (see the uppy computed).
      uploadBusy: false,
      // Bumped on every open and close (see applyDefaultLanguage).
      openCycle: 0,
      // Resumable sessions this browser still holds, refreshed on each open.
      unfinishedUploads: [],
    };
  },
  async mounted() {
    this.refreshUnfinishedUploads();
    await this.checkSubscriptionAndLimit();
  },
  methods: {
    closeImportPopup() {
      analytics.importPopupClosed();
      this.appStore.showImportPopup = false;
    },
    /**
     * Re-read the resumable sessions. Cheap (a localStorage scan) and always
     * read fresh on open, because a session can be created, finished, or
     * expired by the uploader while this popup is closed.
     */
    refreshUnfinishedUploads() {
      this.unfinishedUploads = listUnfinishedUploads();
    },
    onLanguageChange() {
      this.languageTouched = true;
      analytics.importLanguageChanged({
        language: this.selectedLanguage || "auto",
      });
    },
    /**
     * Preselect the saved default (Auto-detect if none), as the mobile apps
     * do; the user can still change it for this upload. Skipped while a file
     * is uploading: changing the language rebuilds the Uppy instance.
     */
    async applyDefaultLanguage() {
      const cycle = this.openCycle;
      this.languageTouched = false;
      const apply = (saved) => {
        // Not once the popup has closed or reopened, and not over the
        // user's own pick.
        if (cycle !== this.openCycle || this.languageTouched) return;
        if (this.hasUnfinishedFiles(this.uppy)) return;
        this.defaultAtOpen = saved;
        this.selectedLanguage = initialLanguage(saved);
      };
      apply(this.languagePreferenceStore.defaultLanguage);
      apply(await this.languagePreferenceStore.load());
    },
    // Files still being added or uploaded; finished and failed ones don't
    // count.
    hasUnfinishedFiles(instance) {
      return (instance?.getFiles?.() ?? []).some(
        (file) => !file?.progress?.uploadComplete && !file?.error,
      );
    },
    syncUploadBusy(instance) {
      // Only the instance on screen decides the lock.
      if (instance === this.uppy) {
        this.uploadBusy = this.hasUnfinishedFiles(instance);
        return;
      }
      // An instance that was still uploading when a rebuild replaced it:
      // retire it as soon as it finishes (retireUppy leaves it alone until
      // then).
      this.retireUppy(instance);
    },
    // Close an instance a rebuild replaced. Never the one on screen, never
    // twice, and never while it's still uploading: closing cancels uploads.
    retireUppy(instance) {
      if (!instance || instance === this.uppy) return;
      if (retiredUppyInstances.has(instance)) return;
      if (this.hasUnfinishedFiles(instance)) return;
      retiredUppyInstances.add(instance);
      instance.close?.({ reason: "unmount" });
    },
    /**
     * Check if we should show the notification opt-in prompt after upload
     */
    async checkNotificationPrompt() {
      // Small delay to let the import popup close animation complete
      setTimeout(async () => {
        console.log("[ImportPopup] Checking notification prompt eligibility");

        // Check feature flag first
        const isFeatureEnabled = analytics.isFeatureEnabled(
          "browser-push-notifications",
        );
        if (!isFeatureEnabled) {
          console.log("[ImportPopup] Feature flag disabled, skipping prompt");
          return;
        }

        // Initialize notification store if needed
        this.notificationStore.initialize();

        // Check if we should show the prompt (backend handles logic)
        const shouldShow = await this.notificationStore.checkPromptStatus();
        if (shouldShow) {
          console.log("[ImportPopup] Showing notification opt-in popup");
          this.notificationStore.openOptInPopup();
        }
      }, 500);
    },
    trackUpgradeClicked() {
      analytics.importUpgradeClicked({
        remaining_uploads: this.remainingFiles,
      });
    },
    async checkSubscriptionAndLimit() {
      try {
        if (!this.workspaceStore.isSubscribedState) {
          // Use the store to check the daily file limit
          await this.workspaceStore.checkDailyFileUploadLimit();
        }

        this.loading = false; // Set loading to false after data is fetched
      } catch (error) {
        // Failed to check subscription status or file upload limit
        this.loading = false; // Ensure the component renders even on error
      }
    },
    /**
     * Drop already-finished files from Uppy's state when the popup reopens,
     * so the next upload starts from a clean slate.
     *
     * Uppy never evicts a file after a successful upload — `upload-success`
     * only marks it complete — and with `maxNumberOfFiles: 1` that leftover
     * blocks every later add on the same instance ("Only one file at a time").
     * Today this only self-heals when a route change happens to rebuild the
     * `uppy` computed; on the conversations route the upload path does NOT
     * navigate, so the completed file lingers and the next upload is refused.
     *
     * In-flight files are left untouched, so the one-at-a-time rule still holds
     * while an upload is actually running. If `this.uppy` re-evaluates to a
     * fresh (empty) instance here, this is a harmless no-op.
     */
    resetFinishedUploads() {
      const instance = this.uppy;
      const finished = (instance?.getFiles?.() ?? []).filter(
        (file) => file?.progress?.uploadComplete || file?.error,
      );
      if (finished.length > 0) {
        instance.removeFiles(
          finished.map((file) => file.id),
          "import-popup-reopened",
        );
      }
    },
  },
  watch: {
    // A rebuilt Uppy instance (the language or account changed) replaces the
    // old one. This runs after render, once the Dashboard has moved to the
    // new instance, so the old one can be closed.
    uppy: {
      handler(current, previous) {
        // The new instance decides whether the picker is locked: the old
        // one's events are ignored from here on, so its state can't stick.
        this.uploadBusy = this.hasUnfinishedFiles(current);
        if (previous && previous !== current) this.retireUppy(previous);
      },
      flush: "post",
    },
    showImportPopup(newVal) {
      // Each open or close starts a new cycle (see applyDefaultLanguage).
      this.openCycle++;
      if (newVal) {
        analytics.importPopupOpened({
          remaining_uploads: this.remainingFiles,
          is_at_limit: this.hasExceededLimit,
        });
        // Clear any file left over from a previous completed upload so the
        // one-at-a-time restriction doesn't block this fresh session.
        this.resetFinishedUploads();
        this.refreshUnfinishedUploads();
        this.applyDefaultLanguage();
        this.checkSubscriptionAndLimit();
      }
    },
    hasExceededLimit(newVal) {
      if (newVal && this.showImportPopup) {
        analytics.importLimitReachedShown({
          daily_limit: this.dailyLimit,
        });
      }
    },
  },
};
</script>

<style src="@uppy/core/dist/style.css"></style>
<style src="@uppy/dashboard/dist/style.css"></style>
<style>
.uppy-Dashboard {
  width: 100%;
  height: 300px;
}

/* Unfinished-upload notice. Informational, not an error: this is a recoverable
   state and the user is one drag away from finishing it. */
.resume-notice {
  margin-bottom: 16px;
  padding: 12px 14px;
  border: 1px solid #ffe0a3;
  border-left: 3px solid #f5a623;
  border-radius: 6px;
  background: #fffaf0;
}

.resume-notice__title {
  margin: 0 0 4px;
  font-weight: 600;
  font-size: 14px;
  color: #6b4a10;
}

.resume-notice__body {
  margin: 0;
  font-size: 13px;
  line-height: 1.45;
  color: #6b4a10;
}

.resume-notice__list {
  margin: 8px 0 0;
  padding-left: 18px;
  font-size: 13px;
  color: #6b4a10;
}

.resume-notice__file {
  font-weight: 600;
  /* Long filenames must not stretch the popup on a phone. */
  overflow-wrap: anywhere;
}

.resume-notice__hint {
  font-weight: 400;
  opacity: 0.85;
}

.exceeded-limit-message {
  text-align: center;
  color: red;
}

.exceeded-limit-message p {
  margin-bottom: 16px;
}

.import-counts {
  text-align: center;
  margin-top: 20px;
}

.btn-holder {
  text-align: center;
  margin-top: 20px;
}

/* Language Selector Styles */
.language-selector-container {
  margin-bottom: 20px;
  padding: 0 2px;
}

.language-label {
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
}

.language-dropdown {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  color: #1f2937;
  background-color: #ffffff;
  cursor: pointer;
  transition: all 0.2s ease;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236b7280' d='M10.293 3.293L6 7.586 1.707 3.293A1 1 0 00.293 4.707l5 5a1 1 0 001.414 0l5-5a1 1 0 10-1.414-1.414z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  padding-right: 36px;
}

.language-dropdown:hover {
  border-color: #9ca3af;
}

.language-dropdown:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.language-dropdown optgroup {
  font-weight: 600;
  color: #6b7280;
}

.language-dropdown option {
  font-weight: normal;
  color: #1f2937;
  padding: 4px 0;
}

/* Dark mode support if needed */
@media (prefers-color-scheme: dark) {
  .language-label {
    color: #d1d5db;
  }

  .language-dropdown {
    background-color: #1f2937;
    color: #f3f4f6;
    border-color: #4b5563;
  }

  .language-dropdown:hover {
    border-color: #6b7280;
  }

  .language-dropdown optgroup {
    color: #9ca3af;
  }

  .language-dropdown option {
    color: #f3f4f6;
    background-color: #1f2937;
  }
}
</style>
